import { GoodsDto, GoodsStatus } from '../../api/ClientApi';

/** Jedna faktura dobavljača sabrana iz robe: sve partije sa istim `uniqueBatchId` kod istog dobavljača. */
export type InvoiceSummary = {
    key: string;
    invoiceNumber: string;
    supplierId?: number;
    supplierName?: string;
    arrivalDate?: Date;
    /** Kada je faktura uneta u sistem (najranije kreiranje partije). */
    enteredDate?: Date;
    articlesCount: number;
    totalValue: number;
    statuses: GoodsStatus[];
};

const STATUS_ORDER: GoodsStatus[] = [
    GoodsStatus.Ordered,
    GoodsStatus.InStock,
    GoodsStatus.InTransport,
    GoodsStatus.Used,
    GoodsStatus.Sold,
    GoodsStatus.WrittenOff,
];

export const getInvoiceKey = (supplierId: number | undefined, invoiceNumber: string) =>
    (supplierId ?? 0) + '|' + invoiceNumber;

const matchesArticleFilter = (goods: GoodsDto, articleTokens: string[]) => {
    if (articleTokens.length === 0) {
        return true;
    }
    const values = [
        goods.article?.title,
        goods.article?.innerCode,
        goods.article?.outterCode,
        goods.article?.manufacturer,
    ]
        .filter((v): v is string => !!v)
        .map((v) => v.toLowerCase());

    return articleTokens.every((token) => values.some((v) => v.includes(token)));
};

/**
 * Grupiše robu po fakturi. Vrednost fakture je zbir količina × nabavna cena preko svih statusa,
 * jer roba koja je ugrađena, prodata ili otpisana i dalje pripada toj fakturi.
 * Filter artikla zadržava fakture koje sadrže bar jedan odgovarajući artikal.
 */
export const buildInvoiceSummaries = (
    goods: GoodsDto[],
    supplierId: number | undefined,
    invoiceFilter: string,
    articleFilter: string,
): InvoiceSummary[] => {
    const invoiceToken = invoiceFilter.trim().toLowerCase();
    const articleTokens = articleFilter
        .toLowerCase()
        .split(' ')
        .filter((t) => t !== '');

    const invoices = new Map<string, InvoiceSummary & { articleIds: Set<number>; articleMatched: boolean }>();

    goods.forEach((g) => {
        const invoiceNumber = g.batch?.uniqueBatchId?.trim();
        const goodsSupplierId = g.batch?.supplier?.id;

        if (!invoiceNumber || (supplierId && goodsSupplierId !== supplierId)) {
            return;
        }
        if (invoiceToken !== '' && !invoiceNumber.toLowerCase().includes(invoiceToken)) {
            return;
        }

        const key = getInvoiceKey(goodsSupplierId, invoiceNumber);
        let invoice = invoices.get(key);

        if (!invoice) {
            invoice = {
                key,
                invoiceNumber,
                supplierId: goodsSupplierId,
                supplierName: g.batch?.supplier?.name,
                arrivalDate: undefined,
                enteredDate: undefined,
                articlesCount: 0,
                totalValue: 0,
                statuses: [],
                articleIds: new Set<number>(),
                articleMatched: false,
            };
            invoices.set(key, invoice);
        }

        const arrival = g.batch?.batchArrivalTime;
        if (arrival && (!invoice.arrivalDate || arrival < invoice.arrivalDate)) {
            invoice.arrivalDate = arrival;
        }
        const entered = g.batch?.created ?? g.created;
        if (entered && (!invoice.enteredDate || entered < invoice.enteredDate)) {
            invoice.enteredDate = entered;
        }
        if (g.article?.id !== undefined) {
            invoice.articleIds.add(g.article.id);
        }
        invoice.totalValue += (g.quantity ?? 0) * (g.batch?.purchasePricePerArticle ?? 0);
        if (g.goodsStatus && !invoice.statuses.includes(g.goodsStatus)) {
            invoice.statuses.push(g.goodsStatus);
        }
        if (!invoice.articleMatched && matchesArticleFilter(g, articleTokens)) {
            invoice.articleMatched = true;
        }
    });

    return Array.from(invoices.values())
        .filter((i) => i.articleMatched)
        .map(({ articleIds, articleMatched, ...invoice }) => ({
            ...invoice,
            articlesCount: articleIds.size,
            statuses: [...invoice.statuses].sort((a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b)),
        }));
};

export type InvoiceSortField =
    | 'invoiceNumber'
    | 'supplierName'
    | 'enteredDate'
    | 'arrivalDate'
    | 'articlesCount'
    | 'totalValue';

export type SortDirection = 'asc' | 'desc';

const getSortValue = (invoice: InvoiceSummary, field: InvoiceSortField): string | number | undefined => {
    switch (field) {
        case 'enteredDate':
        case 'arrivalDate':
            return invoice[field]?.getTime();
        default:
            return invoice[field];
    }
};

/** Prazne vrednosti (bez datuma, bez dobavljača) uvek idu na kraj, bez obzira na smer. */
export const sortInvoices = (invoices: InvoiceSummary[], field: InvoiceSortField, direction: SortDirection) =>
    [...invoices].sort((a, b) => {
        const va = getSortValue(a, field);
        const vb = getSortValue(b, field);

        if (va === undefined || vb === undefined) {
            return va === vb ? 0 : va === undefined ? 1 : -1;
        }
        const result =
            typeof va === 'string' && typeof vb === 'string'
                ? va.localeCompare(vb, 'sr', { numeric: true, sensitivity: 'base' })
                : (va as number) - (vb as number);

        return direction === 'asc' ? result : -result;
    });

/** Roba jedne fakture; bez izabranog dobavljača gleda samo broj fakture. */
export const getGoodsForInvoice = (goods: GoodsDto[], supplierId: number | undefined, invoiceNumber: string) =>
    goods.filter(
        (g) =>
            g.batch?.uniqueBatchId?.trim() === invoiceNumber && (!supplierId || g.batch?.supplier?.id === supplierId),
    );
